import type { CanvasRenderingTarget2D } from 'fancy-canvas';
import type {
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  ISeriesPrimitiveBase,
  LineStyle,
  SeriesAttachedParameter,
  Time,
  UTCTimestamp,
} from 'lightweight-charts';
import type { Candle } from '../types';

interface DrawingPoint {
  time: UTCTimestamp;
  price: number;
}

interface DrawingSegment {
  points: DrawingPoint[];
  color: string;
  width: number;
  style: LineStyle;
  label?: string;
  labelColor?: string;
  labelPosition?: 'left' | 'right';
}

export interface BasicStudyDrawingLineInput {
  values: Array<number | undefined>;
  color: string;
  width: number;
  style: LineStyle;
  label?: string;
  labelColor?: string;
  labelPosition?: 'left' | 'right';
  labelMode?: 'last' | 'all';
  showPriceInLabel?: boolean;
  breakOnValueChange?: boolean;
}

export interface BasicStudyDrawingSegmentInput {
  points: Array<{
    time: UTCTimestamp;
    price: number;
  }>;
  color: string;
  width: number;
  style: LineStyle;
  label?: string;
  labelColor?: string;
  labelPosition?: 'left' | 'right';
}

type PrimitiveAttachment = SeriesAttachedParameter<Time, 'Candlestick'>;

function lineDashForStyle(style: LineStyle): number[] {
  if (style === 1) {
    return [8, 5];
  }
  if (style === 2) {
    return [2, 5];
  }
  return [];
}

class BasicStudyDrawingPaneRenderer implements IPrimitivePaneRenderer {
  public constructor(private readonly source: BasicStudyDrawingPrimitive) {}

  public draw(target: CanvasRenderingTarget2D): void {
    const attachment = this.source.attachment;
    if (!attachment || this.source.segments.length === 0) {
      return;
    }

    const { chart, series } = attachment;

    target.useMediaCoordinateSpace(({ context }) => {
      context.save();
      context.lineCap = 'round';
      context.lineJoin = 'round';
      context.font = '11px sans-serif';
      context.textBaseline = 'middle';

      for (const segment of this.source.segments) {
        const points: { x: number; y: number }[] = [];

        for (const point of segment.points) {
          const x = chart.timeScale().timeToCoordinate(point.time);
          const y = series.priceToCoordinate(point.price);
          if (x === null || y === null) {
            continue;
          }
          points.push({ x, y });
        }

        if (points.length < 2) {
          continue;
        }

        context.beginPath();
        context.setLineDash(lineDashForStyle(segment.style));
        context.lineWidth = segment.width;
        context.strokeStyle = segment.color;
        context.moveTo(points[0].x, points[0].y);
        for (let index = 1; index < points.length; index += 1) {
          context.lineTo(points[index].x, points[index].y);
        }
        context.stroke();

        if (segment.label) {
          const useLeftAnchor = segment.labelPosition === 'left';
          const anchor = useLeftAnchor ? points[0] : points[points.length - 1];
          context.textAlign = useLeftAnchor ? 'right' : 'left';
          context.fillStyle = segment.labelColor ?? segment.color;
          context.fillText(segment.label, anchor.x + (useLeftAnchor ? -6 : 6), anchor.y - 8);
        }
      }

      context.restore();
    });
  }
}

class BasicStudyDrawingPaneView implements IPrimitivePaneView {
  private readonly paneRenderer: BasicStudyDrawingPaneRenderer;

  public constructor(source: BasicStudyDrawingPrimitive) {
    this.paneRenderer = new BasicStudyDrawingPaneRenderer(source);
  }

  public zOrder() {
    return 'top' as const;
  }

  public renderer(): IPrimitivePaneRenderer {
    return this.paneRenderer;
  }
}

export class BasicStudyDrawingPrimitive implements ISeriesPrimitiveBase<PrimitiveAttachment> {
  public attachment: PrimitiveAttachment | null = null;
  public segments: DrawingSegment[] = [];

  private readonly paneView: BasicStudyDrawingPaneView;

  public constructor() {
    this.paneView = new BasicStudyDrawingPaneView(this);
  }

  public attached(param: PrimitiveAttachment): void {
    this.attachment = param;
  }

  public detached(): void {
    this.attachment = null;
  }

  public paneViews() {
    return [this.paneView];
  }

  public setData(candles: Candle[], lines: BasicStudyDrawingLineInput[]): void {
    const nextSegments: DrawingSegment[] = [];

    const flushSegment = (
      points: DrawingPoint[],
      color: string,
      width: number,
      style: LineStyle,
    ) => {
      if (points.length < 2) {
        points.length = 0;
        return;
      }
      nextSegments.push({ points: [...points], color, width, style });
      points.length = 0;
    };

    for (const line of lines) {
      const points: DrawingPoint[] = [];

      for (let index = 0; index < candles.length; index += 1) {
        const value = line.values[index];
        if (value === undefined || !Number.isFinite(value)) {
          flushSegment(points, line.color, line.width, line.style);
          continue;
        }

        const time = Math.floor(new Date(candles[index].time).getTime() / 1000);
        if (!Number.isFinite(time)) {
          flushSegment(points, line.color, line.width, line.style);
          continue;
        }

        points.push({
          time: time as UTCTimestamp,
          price: value,
        });
      }

      flushSegment(points, line.color, line.width, line.style);
    }

    this.segments = nextSegments;
    this.attachment?.requestUpdate();
  }

  public setSegments(segments: BasicStudyDrawingSegmentInput[]): void {
    this.segments = segments
      .map((segment) => ({
        points: segment.points
          .filter((point) => Number.isFinite(point.time) && Number.isFinite(point.price))
          .map((point) => ({ time: point.time, price: point.price })),
        color: segment.color,
        width: segment.width,
        style: segment.style,
        label: segment.label,
        labelColor: segment.labelColor,
        labelPosition: segment.labelPosition,
      }))
      .filter((segment) => segment.points.length >= 2);
    this.attachment?.requestUpdate();
  }
}
